import { storefrontApiRequest } from './shopify';
import { supabase } from '@/integrations/supabase/client';

export type PolicyType =
  | 'PRIVACY_POLICY'
  | 'REFUND_POLICY'
  | 'TERMS_OF_SERVICE'
  | 'SHIPPING_POLICY'
  | 'SUBSCRIPTION_POLICY'
  | 'LEGAL_NOTICE'
  | 'CONTACT_INFORMATION';

export interface ShopPolicy {
  id?: string;
  type: PolicyType;
  title: string;
  body: string;
  url?: string;
  handle?: string;
}

// Hebrew labels for the policies we expose
export const POLICY_LABELS: Record<PolicyType, string> = {
  PRIVACY_POLICY: 'מדיניות פרטיות',
  REFUND_POLICY: 'מדיניות החזרות וביטולים',
  TERMS_OF_SERVICE: 'תקנון האתר',
  SHIPPING_POLICY: 'מדיניות משלוחים',
  SUBSCRIPTION_POLICY: 'תנאי מנוי',
  LEGAL_NOTICE: 'הודעה משפטית',
  CONTACT_INFORMATION: 'פרטי יצירת קשר',
};

// Map URL handles ↔ policy types so footer/route links are stable.
export const HANDLE_TO_TYPE: Record<string, PolicyType> = {
  'privacy-policy': 'PRIVACY_POLICY',
  'refund-policy': 'REFUND_POLICY',
  'terms-of-service': 'TERMS_OF_SERVICE',
  'shipping-policy': 'SHIPPING_POLICY',
  'subscription-policy': 'SUBSCRIPTION_POLICY',
  'legal-notice': 'LEGAL_NOTICE',
  'contact-information': 'CONTACT_INFORMATION',
};

export const TYPE_TO_HANDLE: Record<PolicyType, string> = Object.fromEntries(
  Object.entries(HANDLE_TO_TYPE).map(([h, t]) => [t, h]),
) as Record<PolicyType, string>;

// ===== Public (Storefront API) =====
const STOREFRONT_QUERY = /* GraphQL */ `
  query GetShopPolicies {
    shop {
      privacyPolicy { id title body url handle }
      refundPolicy { id title body url handle }
      termsOfService { id title body url handle }
      shippingPolicy { id title body url handle }
      subscriptionPolicy { id title body url handle }
    }
  }
`;

type StorefrontPolicy = { id: string; title: string; body: string; url: string; handle: string } | null;

export async function fetchPublicPolicies(): Promise<ShopPolicy[]> {
  const data = await storefrontApiRequest<{
    data: {
      shop: {
        privacyPolicy: StorefrontPolicy;
        refundPolicy: StorefrontPolicy;
        termsOfService: StorefrontPolicy;
        shippingPolicy: StorefrontPolicy;
        subscriptionPolicy: StorefrontPolicy;
      };
    };
  }>(STOREFRONT_QUERY);
  const shop = data?.data?.shop;
  if (!shop) return [];
  const mapping: Array<[PolicyType, StorefrontPolicy]> = [
    ['PRIVACY_POLICY', shop.privacyPolicy],
    ['REFUND_POLICY', shop.refundPolicy],
    ['TERMS_OF_SERVICE', shop.termsOfService],
    ['SHIPPING_POLICY', shop.shippingPolicy],
    ['SUBSCRIPTION_POLICY', shop.subscriptionPolicy],
  ];
  return mapping
    .filter(([, p]) => p)
    .map(([type, p]) => ({
      type,
      id: p!.id,
      title: p!.title || POLICY_LABELS[type],
      body: p!.body || '',
      url: p!.url,
      handle: p!.handle,
    }));
}

export async function fetchPublicPolicyByHandle(handle: string): Promise<ShopPolicy | null> {
  const type = HANDLE_TO_TYPE[handle];
  if (!type) return null;
  const list = await fetchPublicPolicies();
  return list.find((p) => p.type === type) ?? null;
}

// ===== Admin (Shopify Admin API via shopify-admin edge function) =====
async function callShopifyAdmin(query: string, variables: Record<string, unknown> = {}) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('No session');
  const { data, error } = await supabase.functions.invoke('shopify-admin', {
    body: { query, variables },
    headers: { Authorization: `Bearer ${token}` },
  });
  if (error) throw error;
  if ((data as any)?.errors) {
    throw new Error((data as any).errors.map((e: any) => e.message).join(', '));
  }
  return data;
}

const ADMIN_LIST_QUERY = /* GraphQL */ `
  {
    shop {
      shopPolicies { id type title body url }
    }
  }
`;

export async function fetchAdminPolicies(): Promise<ShopPolicy[]> {
  const data = await callShopifyAdmin(ADMIN_LIST_QUERY);
  const policies = data?.data?.shop?.shopPolicies ?? [];
  return policies.map((p: any) => ({
    id: p.id,
    type: p.type as PolicyType,
    title: p.title || POLICY_LABELS[p.type as PolicyType] || p.type,
    body: p.body || '',
    url: p.url,
  }));
}

const ADMIN_UPDATE_MUTATION = /* GraphQL */ `
  mutation ShopPolicyUpdate($shopPolicy: ShopPolicyInput!) {
    shopPolicyUpdate(shopPolicy: $shopPolicy) {
      shopPolicy { id type body url }
      userErrors { field message }
    }
  }
`;

export async function updateAdminPolicy(type: PolicyType, body: string): Promise<void> {
  const data = await callShopifyAdmin(ADMIN_UPDATE_MUTATION, {
    shopPolicy: { type, body },
  });
  const errors = data?.data?.shopPolicyUpdate?.userErrors ?? [];
  if (errors.length) {
    throw new Error(errors.map((e: any) => e.message).join(', '));
  }
}
